/**
 * Quantidade de estoque fracionada (migração 0110): 0,5 L de óleo, 30 ml de
 * vitrificador, 12,5 g de cera. Fonte única da API, do painel e do app.
 *
 * O banco guarda 3 casas (`numeric(12,3)`). A conta em `number` do JS não é
 * exata (0,1 + 0,2 = 0,30000000000000004), então toda soma ou subtração de
 * estoque passa por `roundStockQuantity` — senão "sobrou 0,000000001 L" vira
 * item que nunca zera e nunca alerta.
 */

/** Casas decimais que o estoque guarda. */
export const STOCK_QUANTITY_DECIMALS = 3;

/** Maior quantidade que cabe em `numeric(12,3)`. */
export const STOCK_QUANTITY_MAX = 999_999_999;

const FACTOR = 10 ** STOCK_QUANTITY_DECIMALS;

/** Arredonda para as 3 casas do banco. */
export function roundStockQuantity(n: number): number {
  return Math.round(n * FACTOR) / FACTOR;
}

/** A quantidade cabe nas 3 casas do banco, sem perder nada? */
export function hasStockPrecision(n: number): boolean {
  return Number.isFinite(n) && Math.abs(n * FACTOR - Math.round(n * FACTOR)) < 1e-6;
}

/** Quantidade que a API aceita: positiva e com até 3 casas. Formulários usam isto no botão. */
export function isValidStockQuantity(n: number): boolean {
  return Number.isFinite(n) && n > 0 && n <= STOCK_QUANTITY_MAX && hasStockPrecision(n);
}

/** Movimentação manual de estoque (`POST /inventory/:id/movement`). */
export type StockMovementType = 'in' | 'out' | 'adjustment';

/**
 * Saldo depois da movimentação — a mesma conta da API, para a tela dizer
 * "fica com X" antes de gravar. `adjustment` é a contagem da prateleira (o
 * saldo final, não um delta) e aceita zero; entrada e saída exigem quantidade
 * maior que zero. Quantidade inválida → `null`. Saída maior que o saldo volta
 * NEGATIVA, e a API recusa: a tela usa o sinal para explicar o porquê.
 */
export function stockAfterMovement(current: number, type: StockMovementType, qty: number): number | null {
  if (type === 'adjustment') {
    return qty === 0 || isValidStockQuantity(qty) ? roundStockQuantity(qty) : null;
  }
  if (!isValidStockQuantity(qty)) return null;
  return roundStockQuantity(type === 'in' ? current + qty : current - qty);
}

/** Unidades do estoque — o enum `inventory_unit` do banco. */
export const STOCK_UNITS = ['unidade', 'litro', 'ml', 'kg', 'g', 'metro', 'par', 'jogo', 'kit'] as const;
export type StockUnit = (typeof STOCK_UNITS)[number];

export function isStockUnit(value: unknown): value is StockUnit {
  return typeof value === 'string' && (STOCK_UNITS as readonly string[]).includes(value);
}

/**
 * O que o painel anterior à 0110 mandava no campo de unidade ("un", "L",
 * "jg"…): a API descartava, então nunca chegou ao banco — mas uma aba aberta
 * com o painel antigo ainda manda. Traduzido, em vez de recusado.
 */
const LEGACY_UNIT_ALIASES: Record<string, StockUnit> = {
  un: 'unidade',
  jg: 'jogo',
  l: 'litro',
  m: 'metro',
};

/** A unidade que chegou, como unidade do estoque — ou `undefined` (vazia ou desconhecida: não mexe). */
export function normalizeStockUnit(raw: unknown): StockUnit | undefined {
  if (typeof raw !== 'string') return undefined;
  const v = raw.trim();
  if (isStockUnit(v)) return v;
  return LEGACY_UNIT_ALIASES[v.toLowerCase()];
}

/** Nome da unidade para o seletor ("Mililitro (ml)"). */
export const STOCK_UNIT_LABELS: Record<StockUnit, string> = {
  unidade: 'Unidade',
  litro: 'Litro (L)',
  ml: 'Mililitro (ml)',
  kg: 'Quilo (kg)',
  g: 'Grama (g)',
  metro: 'Metro (m)',
  par: 'Par',
  jogo: 'Jogo',
  kit: 'Kit',
};

/** Sigla ao lado do número: singular e plural. Unidade desconhecida = "un". */
const SHORT: Record<StockUnit, [string, string]> = {
  unidade: ['un', 'un'],
  litro: ['L', 'L'],
  ml: ['ml', 'ml'],
  kg: ['kg', 'kg'],
  g: ['g', 'g'],
  metro: ['m', 'm'],
  par: ['par', 'pares'],
  jogo: ['jogo', 'jogos'],
  kit: ['kit', 'kits'],
};

/**
 * Unidades que se vendem em fração — a quantidade se digita, não se conta
 * no −/+. "par", "jogo" e "kit" são contáveis como "unidade".
 */
const FRACTIONAL_UNITS: readonly StockUnit[] = ['litro', 'ml', 'kg', 'g', 'metro'];

export function isFractionalStockUnit(unit: unknown): unit is StockUnit {
  return isStockUnit(unit) && FRACTIONAL_UNITS.includes(unit);
}

/** Sigla da unidade ao lado de um campo ("L", "ml"). Desconhecida = "un". */
export function stockUnitShort(unit: string | null | undefined): string {
  return (isStockUnit(unit) ? SHORT[unit] : SHORT.unidade)[1];
}

/** "0,5" / "0.5" digitado → 0,5. Vazio ou inválido → NaN. */
export function parseStockQuantityText(text: string): number {
  const t = text.trim().replace(',', '.');
  if (!/^\d+(\.\d*)?$|^\.\d+$/.test(t)) return NaN;
  return Number(t);
}

/** "1,5" — o número no formato brasileiro, até 3 casas, sem zeros à toa. */
export function formatStockNumber(qty: number): string {
  return roundStockQuantity(qty).toLocaleString('pt-BR', { maximumFractionDigits: STOCK_QUANTITY_DECIMALS });
}

/** "1,5 L", "30 ml", "2 pares", "4 un". */
export function formatStockQuantity(qty: number, unit: string | null | undefined): string {
  const [one, many] = isStockUnit(unit) ? SHORT[unit] : SHORT.unidade;
  return `${formatStockNumber(qty)} ${Math.abs(qty) === 1 ? one : many}`;
}
